import React, { useEffect, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { FormPayload, PreviewPageType, Template } from '@dev/ee-api/ee-api-models';
import { SimplePopover, SecondaryButton, Typography, palette, TertiaryButton, DashboardCustomizeIcon, ResetIcon } from '@dev/ee-components';
import { Link } from '@material-ui/core';
import { isEmpty, isString } from 'lodash-es';
import { getApiPath } from '@/services/Secuirty';
import { DefaultActivationTemplateScreenshot } from '@/services/icons/DefaultActivationTemplate';

import { UpdateDialog } from '@/store/dialog/actions';
import { getLandingPageList } from '@/store/landingPageList/actions';
import OpenInNew from '@/services/icons/open-in-new-icon.svg';

import { useSecurity } from '@/services/hooks/useSecurity';
import { links } from '@/services/dictionaries/links';
import { getPaidNewsletterCreatorStore, LoadPageTemplateStart, TemplateLoadSingleStart } from '@/store/paidNewsletterCreator/actions';
import { FormPageDropdownWrapper, DisplayedScreenLine, ScreenDropdownWrapper, OpenInNewWrapper, NoDataWrapper } from './styles';
import { FormMultiDoubleOptInCustomizeModalBodyProps, ModalState, DoubleOptInPageTypeEnum } from '../../types';
import { pageTypeOptions } from '../../contants';
import { ScreenshotWrapper, TemplateButtonsWrapper } from '../../styles';
import { FormMultiDoubleOptInLandingPage } from '../FormMultiDoubleOptInLandingPage';
import { FormMultiDoubleOptInTemplatesGallery } from '../FormMultiDoubleOptInTemplatesGallery';
import { FormMultiDoubleOptInExternalWebsite } from '../FormMultiDoubleOptInExternalWebsite';
import { changeDoubleOptInPageTypeAction, modalStateReducer, openGalleryAction, resetAction, setActivationTemplateScreenshotAction, setDoubleOptInPagePreviewUrlAction } from '../../state';

const getIsPreviewDisabled = (modalState: ModalState, isLoadingDoubleOptInPage: boolean, isLandingPageData: boolean, defaultPreview?: string): boolean => {
    if (modalState.doubleOptInPageType === DoubleOptInPageTypeEnum.Defaultpage) {
        return !defaultPreview;
    }
    if (modalState.doubleOptInPageType === DoubleOptInPageTypeEnum.Landingpage) {
        return !isLandingPageData || isString(modalState.searchInput || isLoadingDoubleOptInPage);
    }

    return modalState.isDoubleOptInPageError;
}

const getDefaultDoubleOptInPageType = (templateFormPayload?: FormPayload): DoubleOptInPageTypeEnum => {
    if (!templateFormPayload || !templateFormPayload.DoubleOptInPage) {
        return DoubleOptInPageTypeEnum.Defaultpage;
    }
    if (templateFormPayload.DoubleOptInPage.startsWith('https://')) {
        return DoubleOptInPageTypeEnum.Externalwebsite;
    }
    return DoubleOptInPageTypeEnum.Landingpage;
}
const getIsTemplateSelected = (modalState: ModalState): boolean => !!modalState?.activationTemplateName;


export const FormMultiDoubleOptInCustomizeModalBody = ({
    updateFormPayload,
    templateFormPayload,
    historyBackAction
 }: FormMultiDoubleOptInCustomizeModalBodyProps): JSX.Element => {
    const dispatch = useDispatch<any>();
    const isFirstFormPayloadChange = useRef<boolean>(true);

    const [modalState, dispatchModalState] = useReducer(modalStateReducer, {
        isGalleryOpen: false,
        doubleOptInPageType: getDefaultDoubleOptInPageType(templateFormPayload),
        isLoadingActivationTemplate: !!templateFormPayload?.ActivationTemplateName,
        activationTemplateName: templateFormPayload?.ActivationTemplateName,
        doubleOptInPageValue: templateFormPayload?.DoubleOptInPage,
        isLoadingDoubleOptInPage: !!templateFormPayload?.DoubleOptInPage,
    });

    const { t } = useTranslation();
    const { pageTemplates } = useSelector(getPaidNewsletterCreatorStore);
    const { isReseller } = useSecurity();

    const { templatesList, loadingTemplates } = useSelector(getLandingPageList);
    const isLandingPageData = !loadingTemplates && !isEmpty(templatesList);

    useEffect(() => {
        if (isFirstFormPayloadChange.current) {
            isFirstFormPayloadChange.current = false;
            return;
        }
        updateFormPayload({
            ActivationTemplateName: modalState.activationTemplateName,
            DoubleOptInPage: modalState.doubleOptInPageType === DoubleOptInPageTypeEnum.Landingpage && !modalState.doubleOptInPageValue ? templatesList?.[0]?.Name : modalState.doubleOptInPageValue,
        });
    }, [modalState.activationTemplateName, modalState.doubleOptInPageValue, modalState.doubleOptInPageType, templatesList])
    
    useEffect((): void => { dispatch(UpdateDialog({ isDisabled: isString(modalState.searchInput) || modalState.isDoubleOptInPageError })); }, [modalState.isDoubleOptInPageError, modalState.searchInput]);

    useEffect((): void => {
        if (modalState.activationTemplateName) {
            //LOAD SAVED ACTIVATION TEMPLATE TO HAVE IT'S SCREENSHOT:
            dispatch(TemplateLoadSingleStart(modalState.activationTemplateName, false)).then(({ template } : { template: Template }) => {
                dispatchModalState(setActivationTemplateScreenshotAction(template.Screenshot));
            }).catch(() => {
                dispatchModalState(setActivationTemplateScreenshotAction(null));
            });
        }
        if (modalState.doubleOptInPageType !== DoubleOptInPageTypeEnum.Landingpage) {
            return;
        }
        //LOAD SAVED DOUBLE OPT-IN TO HAVE IT'S PREVIEW LINK:
        dispatch(TemplateLoadSingleStart(modalState.doubleOptInPageValue, false)).then(({ template } : { template: Template }) => {
            dispatchModalState(setDoubleOptInPagePreviewUrlAction(template.Link));
        }).catch(() => {
            dispatchModalState(setDoubleOptInPagePreviewUrlAction(null));
        });
    }, []);
    useEffect((): void => {
        if (pageTemplates?.activationViaEmailPage) {
            return;
        }
        dispatch(LoadPageTemplateStart(PreviewPageType.ActivationViaEmail));
    }, []);

    const openPreview = (): void => {
        if (modalState.doubleOptInPageType === DoubleOptInPageTypeEnum.Defaultpage) {
            pageTemplates?.activationViaEmailPage && window.open(
                URL.createObjectURL(
                    new Blob([pageTemplates?.activationViaEmailPage], { type: "text/html" })
                ),
                '_blank'
            );
            return;
        }
        if (modalState.doubleOptInPageType === DoubleOptInPageTypeEnum.Externalwebsite) {
            modalState.doubleOptInPageValue && window.open(modalState.doubleOptInPageValue, '_blank');
            return;
        }
        const landingPreviewUrl = modalState.previewUrl || templatesList?.[0]?.Link;
        if (!landingPreviewUrl) {
            return;
        }
        window.open(landingPreviewUrl, '_blank');
    };

    const changeDoubleOptInPageType = (ev, obj): void => {
        const pageType = obj.value as DoubleOptInPageTypeEnum;
        dispatchModalState(changeDoubleOptInPageTypeAction(pageType));
    };

    const onOpenGalleryClick = (): void => {
        dispatchModalState(openGalleryAction);
    }

    const onResetToDefault = (): void => {
        dispatchModalState(resetAction);
    };

    const isPreviewDisabled = getIsPreviewDisabled(modalState, modalState.isLoadingDoubleOptInPage, isLandingPageData, pageTemplates?.activationViaEmailPage);
    const isTemplateSelected = getIsTemplateSelected(modalState);
    return (
        <>
            <ScreenshotWrapper>
                {modalState.activationTemplateName ? <img src={`${getApiPath()}/userfile${encodeURI(modalState.activationTemplateScreenshot)}`} /> : <DefaultActivationTemplateScreenshot />}
            </ScreenshotWrapper>
            <TemplateButtonsWrapper>
                <TertiaryButton
                        startIcon={<DashboardCustomizeIcon color={palette.indigo[6]} width={15} height={15} />}
                        onClick={onOpenGalleryClick}
                        text={isTemplateSelected ? t('useanothertemplate') : t('usemytemplate')}
                />
                <TertiaryButton
                    startIcon={<ResetIcon color={palette.indigo[6]} width={17} height={17} />}
                    onClick={onResetToDefault}
                    text={t('resettodefault')}
                />
            </TemplateButtonsWrapper>
            <FormPageDropdownWrapper>
                <DisplayedScreenLine>
                    <ScreenDropdownWrapper>
                        <SimplePopover
                            type="input"
                            id={'activation-page-dropdown'}
                            label={t('displayscreenaftercontactactivates')}
                            onItemClick={changeDoubleOptInPageType}
                            value={modalState.doubleOptInPageType}
                            fullWidth
                            options={pageTypeOptions()}
                        />
                    </ScreenDropdownWrapper>
                    <SecondaryButton
                        id="preview-button"
                        text={t('preview')}
                        endIcon={
                            <OpenInNewWrapper>
                                <OpenInNew />
                            </OpenInNewWrapper>
                        }
                        onClick={openPreview}
                        disabled={isPreviewDisabled}
                    />
                </DisplayedScreenLine>
                {modalState.doubleOptInPageType === DoubleOptInPageTypeEnum.Landingpage && (
                    <FormMultiDoubleOptInLandingPage 
                        doubleOptInPageValue={modalState.doubleOptInPageValue}
                        searchInput={modalState.searchInput}
                        dispatchModalState={dispatchModalState}
                    />
                )}
                {modalState.doubleOptInPageType === DoubleOptInPageTypeEnum.Landingpage && !loadingTemplates && !isLandingPageData && (
                    <NoDataWrapper>
                        <Typography variant="body1">
                            {t('youneedtocreatelandingfirst')}{' '}
                            {!isReseller && (
                                <Link target="_blank" style={{ color: palette.indigo[6] }} href={links.manageLandingPage}>
                                    {t('learnmore')}
                                </Link>
                            )}
                        </Typography>
                    </NoDataWrapper>
                )}
                {modalState.doubleOptInPageType === DoubleOptInPageTypeEnum.Externalwebsite && (
                   <FormMultiDoubleOptInExternalWebsite 
                        doubleOptInPageValue={modalState.doubleOptInPageValue} 
                        isDoubleOptInPageError={modalState.isDoubleOptInPageError} 
                        dispatchModalState={dispatchModalState} 
                    />
                )}
                {modalState.isGalleryOpen && <FormMultiDoubleOptInTemplatesGallery dispatchModalState={dispatchModalState} historyBackAction={historyBackAction} />}
            </FormPageDropdownWrapper>
        </>
    );
};
